import { ShortTag, Tag } from '../redux/actions/tagActions/types';
import { FavoriteProductTag } from '../redux/reducers/FavoriteProductTagReducer/FavoriteProductTagSlice';
import { LiveGuidelineProductTag } from '../redux/reducers/liveGuidelineProductTagReducer/LiveGuidelineProductTagSlice';

export const TAG_NAME_MAX_LENGTH = 40;

/**
 * Shorten a Live Guideline name to fit a tag: names over 40 chars are cut back to the last whole word
 * within 38 chars (or at 38 chars if there is no space) and end with " …".
 */
export function truncateLiveGuidelineTagName(name: string): string {
    if (name.length <= TAG_NAME_MAX_LENGTH) {
        return name;
    }
    const limit = TAG_NAME_MAX_LENGTH - 2;
    const head = name.slice(0, limit);
    const endsAtWordBoundary = name[limit] === ' ';
    const lastSpace = head.lastIndexOf(' ');
    const cut = endsAtWordBoundary || lastSpace <= 0 ? head : head.slice(0, lastSpace);
    return `${cut.trimEnd()} \u2026`;
}

/**
 * Split tags into common (no organizationId) and org-specific, and sort common tags
 * with business priority: Good, Better, Best first (case-insensitive), then alphabetical.
 */
export function splitAndSortCommonTags(tags: Array<Tag>, order = 'asc') {
    return splitAndSortCommon<Tag>(
        tags,
        (t) => t.id,
        (t) => t.name,
        (t) => t.organizationId,
        order,
    );
}


export function splitAndSortCommonProductTags(tags: Array<FavoriteProductTag>, order = 'asc') {
    return splitAndSortCommon<FavoriteProductTag>(
        tags,
        (pt) => pt.tagId,
        (pt) => pt.tag?.name ?? '',
        (pt) => pt.tag?.organizationId,
        order,
    );
}

export function splitAndSortCommonLiveGuidelineProductTags(
    tags: Array<LiveGuidelineProductTag>,
    order = 'asc',
) {
    return splitAndSortCommon<LiveGuidelineProductTag>(
        tags,
        (pt) => pt.tagId,
        (pt) => pt.tag?.name ?? '',
        (pt) => pt.tag?.organizationId,
        order,
    );
}


/**
 * Generic splitter/sorter for Tag-like collections.
 * - `getName` should return the tag name to sort by
 * - `getOrgId` should return the organizationId (falsy => common tag)
 */
export function splitAndSortCommon<T = any>(
    items: Array<T>,
    getTagId: (item: T) => number,
    getName: (item: T) => string | undefined,
    getOrgId: (item: T) => any,
    order: string,
): Array<T> {
    const all = items ?? [];
    const common = all.filter((t) => !getOrgId(t));
    const orgTags = all.filter((t) => getOrgId(t));

    const customSort = (arr: Array<T>) => {
        return arr.slice().sort((a, b) => {
            return getTagId(a) - getTagId(b);
        });
    }
    const commonSorted = customSort(common);

    const orgSorted = orgTags.slice().sort((a, b) => {
        const aName = (getName?.(a) || '').toLowerCase();
        const bName = (getName?.(b) || '').toLowerCase();
        return order === 'asc' ?
            aName.localeCompare(bName, undefined, { sensitivity: 'base' }) :
            bName.localeCompare(aName, undefined, { sensitivity: 'base' });
    });
    return [...commonSorted, ...orgSorted];
}

const PRIORITY_TAG_NAMES = new Set(['good', 'better', 'best']);

export const splitAndSortShortTags = (tags: Array<ShortTag>, order = 'asc') => {
    const priority: Array<ShortTag> = [];
    const rest: Array<ShortTag> = [];

    for (const tag of tags) {
        if (PRIORITY_TAG_NAMES.has(tag.name.toLowerCase())) {
            priority.push(tag);
        } else {
            rest.push(tag);
        }
    }

    priority.sort((a, b) => a.tagId - b.tagId);

    rest.sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        return order === 'asc'
            ? aName.localeCompare(bName, undefined, { sensitivity: 'base' })
            : bName.localeCompare(aName, undefined, { sensitivity: 'base' });
    });

    return [...priority, ...rest];
};